/**
 * Data processing and manipulation utilities
 */

// Sort data by month number
export const sortDataByMonth = <T extends { شماره_ماه?: number; yearMonth?: number }>(
  data: T[]
): T[] => {
  return [...data].sort((a, b) => {
    const aMonth = a.شماره_ماه || a.yearMonth || 0;
    const bMonth = b.شماره_ماه || b.yearMonth || 0;
    return aMonth - bMonth;
  });
};

// Get unique months from data array and sort them
export const getUniqueMonths = <T extends { month: string; yearMonth?: number }>(
  data: T[]
): string[] => {
  return Array.from(new Set(data.map(d => d.month)))
    .sort((a, b) => {
      const aItem = data.find(d => d.month === a);
      const bItem = data.find(d => d.month === b);
      if (aItem && bItem && aItem.yearMonth && bItem.yearMonth) {
        return aItem.yearMonth - bItem.yearMonth;
      }
      return 0;
    });
};

// Calculate best performing item from array
export const findBestPerforming = <T extends Record<string, any>>(
  data: T[],
  groupKey: string,
  valueKey: string
): { name: string; value: number } => {
  const grouped = data.reduce((acc, item) => {
    const key = item[groupKey];
    if (!acc[key]) {
      acc[key] = { total: 0, count: 0 };
    }
    acc[key].total += item[valueKey] || 0;
    acc[key].count += 1;
    return acc;
  }, {} as Record<string, { total: number; count: number }>);

  let bestName = '';
  let bestValue = 0;

  Object.entries(grouped).forEach(([name, data]) => {
    const avg = data.total / data.count;
    if (avg > bestValue) {
      bestValue = avg;
      bestName = name;
    }
  });

  return { name: bestName, value: bestValue };
};

// Create time range months mapping
export const getTimeRangeMonths = (timeRange: string): number => {
  switch (timeRange) {
    case 'last1Month': return 1;
    case 'last3Months': return 3;
    case 'last6Months': return 6;
    case 'lastYear': return 12;
    default: return 6;
  }
};

// Group data by specific field
export const groupDataBy = <T extends Record<string, any>>(
  data: T[],
  groupField: string
): Record<string, T[]> => {
  return data.reduce((acc, item) => {
    const key = item[groupField];
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {} as Record<string, T[]>);
};

// Calculate percentage difference between two values
export const calculatePercentageDiff = (current: number, previous: number): number => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
};

// Find data point by month with flexible matching
export const findDataByMonth = <T extends { ماه?: string; month?: string }>(
  data: T[],
  targetMonth: string
): T | undefined => {
  return data.find(item => {
    const itemMonth = item.ماه || item.month || '';
    const itemMonthName = itemMonth.includes('-') ? itemMonth.split('-')[1] : itemMonth;
    const targetMonthName = targetMonth.includes('-') ? targetMonth.split('-')[1] : targetMonth;
    
    // Multiple matching strategies
    return (
      itemMonth === targetMonth || // Exact match
      itemMonthName === targetMonthName || // Month name only match
      (targetMonth.includes('-') && itemMonth.includes(targetMonthName)) || // Target has prefix, item contains name
      (!targetMonth.includes('-') && itemMonth.endsWith(targetMonth)) // Target is name only, item ends with it
    );
  });
};

// Create datasets for chart from array data
export const createDatasetFromArray = <T extends Record<string, any>>(
  data: T[],
  labelField: string,
  valueField: string,
  colors: string[]
) => ({
  labels: data.map(item => item[labelField]),
  data: data.map(item => item[valueField]),
  backgroundColor: colors,
  borderColor: colors,
  borderWidth: 1,
});

// Persian month names
export const PERSIAN_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

// Month index mapping
export const MONTH_INDEX_MAP = {
  'فروردین': 1, 'اردیبهشت': 2, 'خرداد': 3, 'تیر': 4, 'مرداد': 5, 'شهریور': 6,
  'مهر': 7, 'آبان': 8, 'آذر': 9, 'دی': 10, 'بهمن': 11, 'اسفند': 12
};